/**
 * Maestro Verified device catalog — normalize API payloads, display names, grouping, matching.
 */

export type VerifiedTier = 'curated' | 'field_verified' | 'under_review';

export interface VerifiedFormatCapabilities {
  max_sample_rate?: number | null;
  max_bit_depth?: number | null;
  dsd_capability?: string | null;
}

export interface VerifiedDeviceEntry {
  id?: string;
  canonical_name: string;
  device_name: string;
  manufacturer: string;
  verified_transports: string[];
  verified_at: string | null;
  tier?: VerifiedTier;
  verified_formats?: VerifiedFormatCapabilities | null;
  manufacturer_url?: string | null;
  support_url?: string | null;
  product_url?: string | null;
  image_url?: string | null;
}

export interface RawCertifiedDevice {
  id?: string;
  display_name?: string | null;
  device_name?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  device_class?: string | null;
  discovery_method?: string | null;
  transport_type?: string | null;
  has_raat?: boolean | null;
  responds_to_sood?: boolean | null;
  last_verified_at?: string | null;
  last_confirmed_at?: string | null;
  verified_at?: string | null;
  canonical_name?: string | null;
  verified_transports?: string[] | null;
  transports_verified?: string[] | null;
  tier?: VerifiedTier | null;
  verified_formats?: VerifiedFormatCapabilities | null;
  max_sample_rate?: number | null;
  max_bit_depth?: number | null;
  dsd_capability?: string | null;
  manufacturer_url?: string | null;
  support_url?: string | null;
  product_url?: string | null;
  image_url?: string | null;
}

const LOCALE_PREFIX_RE = /^[a-z]{2}(?:[-_][A-Za-z]{2,})\s+/i;

const LOCALE_SPEAKER_PREFIXES = [
  /^speakers\s*\(/i,
  /^lautsprecher\s*\(/i,
  /^högtalare\s*\(/i,
  /^hogtalare\s*\(/i,
  /^alto-falantes\s*\(/i,
  /^altavoces\s*\(/i,
  /^enceintes\s*\(/i,
];

const RINCON_PATTERN = /\bRINCON_[A-F0-9]{8,}\b/gi;
const SERIAL_SUFFIX = /\s*(?:\(#?[A-Z0-9]{6,}\)|[#(]\s*[A-Z0-9]{6,}\)?)\s*$/i;
const IP_SUFFIX = /\s*(?:\(|@|,)\s*\d{1,3}(?:\.\d{1,3}){3}(?:\.\d+)?\s*\)?\s*$/;
const TRAILING_PAREN = /\)\s*$/;

/** Single-token display names that are never meaningful device models. */
const DEGENERATE_DISPLAY_TOKENS = new Set([
  'audio',
  'device',
  'renderer',
  'speaker',
  'player',
  'streamer',
  'unknown',
  'unnamed',
  'mediarenderer',
]);

export function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

export function stripLocaleSpeakerPrefix(name: string): string {
  let result = name.trim();
  for (const pattern of LOCALE_SPEAKER_PREFIXES) {
    if (pattern.test(result)) {
      result = result.replace(pattern, '').replace(TRAILING_PAREN, '').trim();
      break;
    }
  }
  return result;
}

export function stripSerialsRinconIp(name: string): string {
  return collapseWhitespace(
    name
      .replace(RINCON_PATTERN, '')
      .replace(IP_SUFFIX, '')
      .replace(SERIAL_SUFFIX, '')
      .trim(),
  );
}

/** Strip padding and locale-prefixed discovery names (e.g. "  en_US PerfectWave DAC "). */
export function cleanDisplayName(raw: string | null | undefined): string {
  if (!raw) return '';
  return collapseWhitespace(
    stripSerialsRinconIp(stripLocaleSpeakerPrefix(String(raw)))
      .replace(LOCALE_PREFIX_RE, ''),
  );
}

export function normalizeRendererNameForMatch(raw: string | null | undefined): string {
  return cleanDisplayName(raw).toLowerCase();
}

export function isDegenerateDisplayName(raw: string | null | undefined): boolean {
  const cleaned = cleanDisplayName(raw);
  if (!cleaned) return true;
  const tokens = cleaned.split(/\s+/);
  if (tokens.length === 1 && DEGENERATE_DISPLAY_TOKENS.has(tokens[0].toLowerCase())) {
    return true;
  }
  return false;
}

export function isBrandLevelEntry(device: VerifiedDeviceEntry): boolean {
  const name = cleanDisplayName(device.device_name).toLowerCase();
  const mfr = cleanDisplayName(device.manufacturer).toLowerCase();
  return Boolean(name && mfr && name === mfr);
}

export function verifiedCardKey(device: VerifiedDeviceEntry): string {
  return `${device.manufacturer}|${device.device_name}`.toLowerCase();
}

export function nextExclusiveExpandedKey(current: string | null, clicked: string): string | null {
  return current === clicked ? null : clicked;
}

function mapTransportVerifiedTag(tag: string): string {
  switch (tag.toLowerCase().trim()) {
    case 'upnp_av':
    case 'openhome':
      return 'Network';
    case 'sonos':
      return 'Sonos';
    case 'bluesound':
      return 'Bluesound';
    case 'raat_only':
      return 'RAAT';
    case 'usb':
    case 'usb_exclusive':
      return 'USB';
    case 'airplay':
      return 'AirPlay';
    default:
      return tag
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
  }
}

/** Full-word transport labels for expanded verified cards. */
export function formatVerifiedTransportLong(tag: string): string {
  const key = tag.toLowerCase().trim();
  switch (key) {
    case 'usb':
    case 'usb exclusive':
    case 'usb_exclusive':
      return 'USB exclusive';
    case 'network':
    case 'upnp_av':
    case 'openhome':
      return 'Network (UPnP)';
    case 'sonos':
      return 'Sonos';
    case 'bluesound':
      return 'Bluesound';
    case 'raat':
    case 'raat_only':
      return 'RAAT';
    case 'airplay':
      return 'AirPlay';
    case 'sood':
      return 'SOOD';
    default:
      return mapTransportVerifiedTag(tag);
  }
}

export function formatVerifiedTierLabel(tier: VerifiedTier | undefined): string {
  if (tier === 'field_verified') return 'Field verified';
  if (tier === 'curated') return 'Maestro curated';
  return 'Under review';
}

function extractVerifiedFormats(raw: RawCertifiedDevice): VerifiedFormatCapabilities | null {
  if (raw.verified_formats && typeof raw.verified_formats === 'object') {
    return raw.verified_formats;
  }
  const hasRate = raw.max_sample_rate != null;
  const hasDepth = raw.max_bit_depth != null;
  const hasDsd = raw.dsd_capability != null && raw.dsd_capability !== 'none';
  if (!hasRate && !hasDepth && !hasDsd) return null;
  return {
    max_sample_rate: raw.max_sample_rate ?? null,
    max_bit_depth: raw.max_bit_depth ?? null,
    dsd_capability: raw.dsd_capability ?? null,
  };
}

export function formatSampleRateLabel(rate: number): string {
  if (rate >= 1000) {
    const khz = rate / 1000;
    return `${khz % 1 === 0 ? khz.toFixed(0) : khz.toFixed(1)} kHz`;
  }
  return `${rate} Hz`;
}

export function hasVerifiedFormatDetails(formats: VerifiedFormatCapabilities | null | undefined): boolean {
  if (!formats) return false;
  return Boolean(
    formats.max_sample_rate
    || formats.max_bit_depth
    || (formats.dsd_capability && formats.dsd_capability !== 'none'),
  );
}

export function deriveTransportTags(device: RawCertifiedDevice): string[] {
  if (Array.isArray(device.transports_verified) && device.transports_verified.length > 0) {
    return [...new Set(device.transports_verified.map(mapTransportVerifiedTag).filter(Boolean))];
  }

  if (Array.isArray(device.verified_transports) && device.verified_transports.length > 0) {
    return [...new Set(device.verified_transports.map((t) => String(t).trim()).filter(Boolean))];
  }

  const tags = new Set<string>();
  const deviceClass = String(device.device_class || '').toLowerCase();
  const discovery = String(device.discovery_method || '').toLowerCase();
  const transport = String(device.transport_type || '').toLowerCase();
  const manufacturer = String(device.manufacturer || '').toLowerCase();

  if (deviceClass === 'usb_dac' || transport === 'usb') tags.add('USB');
  if (discovery === 'sonos' || manufacturer === 'sonos') tags.add('Sonos');
  if (discovery === 'bluesound' || manufacturer === 'bluesound') tags.add('Bluesound');
  if (device.has_raat) tags.add('RAAT');
  if (device.responds_to_sood) tags.add('SOOD');
  if (discovery === 'ssdp' || discovery === 'upnp' || discovery === 'sood' || transport === 'openhome') {
    tags.add('Network');
  }
  if (tags.size === 0 && deviceClass !== 'usb_dac') tags.add('Network');
  if (tags.size === 0 && deviceClass === 'usb_dac') tags.add('USB');

  return [...tags];
}

export function normalizeVerifiedDevice(raw: RawCertifiedDevice): VerifiedDeviceEntry | null {
  const displayName = cleanDisplayName(raw.display_name);
  if (isDegenerateDisplayName(displayName || raw.device_name || raw.model)) {
    return null;
  }

  const manufacturer = cleanDisplayName(raw.manufacturer);
  const model = cleanDisplayName(raw.model);
  const deviceName = displayName || cleanDisplayName(raw.device_name) || model;
  if (!manufacturer && !deviceName) return null;

  const canonical = cleanDisplayName(raw.canonical_name)
    || (manufacturer && deviceName ? `${manufacturer} ${deviceName}` : deviceName || manufacturer);

  const verifiedAt = raw.last_confirmed_at || raw.verified_at || raw.last_verified_at || null;

  return {
    id: raw.id,
    canonical_name: canonical,
    device_name: deviceName || canonical,
    manufacturer: manufacturer || 'Unknown',
    verified_transports: deriveTransportTags(raw),
    verified_at: verifiedAt,
    tier: raw.tier || 'curated',
    verified_formats: extractVerifiedFormats(raw),
    manufacturer_url: raw.manufacturer_url || null,
    support_url: raw.support_url || null,
    product_url: raw.product_url || null,
    image_url: raw.image_url || null,
  };
}

export function normalizeVerifiedPayload(payload: unknown): VerifiedDeviceEntry[] {
  const rows = extractDeviceRows(payload);
  const seen = new Set<string>();
  const out: VerifiedDeviceEntry[] = [];

  for (const row of rows) {
    const displayName = cleanDisplayName(row.display_name)
      || cleanDisplayName(row.device_name)
      || cleanDisplayName(row.model);

    if (isDegenerateDisplayName(displayName)) {
      console.warn('[Maestro Verified] Hiding degenerate catalog row:', JSON.stringify(row));
      continue;
    }

    const normalized = normalizeVerifiedDevice(row);
    if (!normalized) continue;

    const key = verifiedCardKey(normalized);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(normalized);
  }

  out.sort((a, b) => {
    const mfr = a.manufacturer.localeCompare(b.manufacturer);
    if (mfr !== 0) return mfr;
    const brandOrder = Number(isBrandLevelEntry(b)) - Number(isBrandLevelEntry(a));
    if (brandOrder !== 0) return brandOrder;
    return a.device_name.localeCompare(b.device_name);
  });

  return out;
}

function extractDeviceRows(payload: unknown): RawCertifiedDevice[] {
  if (Array.isArray(payload)) return payload as RawCertifiedDevice[];
  if (payload && typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    if (Array.isArray(obj.devices)) return obj.devices as RawCertifiedDevice[];
    if (Array.isArray(obj.items)) return obj.items as RawCertifiedDevice[];
  }
  return [];
}

export type VerifiedDevicesByManufacturer = {
  manufacturer: string;
  devices: VerifiedDeviceEntry[];
};

export function groupByManufacturer(devices: VerifiedDeviceEntry[]): VerifiedDevicesByManufacturer[] {
  const map = new Map<string, VerifiedDeviceEntry[]>();
  for (const device of devices) {
    const mfr = device.manufacturer || 'Other';
    if (!map.has(mfr)) map.set(mfr, []);
    map.get(mfr)!.push(device);
  }
  return [...map.entries()]
    .map(([manufacturer, group]) => ({
      manufacturer,
      devices: group.sort((a, b) => {
        const brandOrder = Number(isBrandLevelEntry(b)) - Number(isBrandLevelEntry(a));
        if (brandOrder !== 0) return brandOrder;
        return a.device_name.localeCompare(b.device_name);
      }),
    }))
    .sort((a, b) => a.manufacturer.localeCompare(b.manufacturer));
}

export function filterVerifiedDevices(devices: VerifiedDeviceEntry[], query: string): VerifiedDeviceEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return devices;
  return devices.filter((d) => {
    const hay = `${d.manufacturer} ${d.device_name} ${d.canonical_name} ${d.verified_transports.join(' ')}`.toLowerCase();
    return hay.includes(q);
  });
}

export function formatVerifiedDate(iso: string | null | undefined, locale?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
  } catch {
    return date.toISOString().slice(0, 10);
  }
}

export function formatVerifiedMonthYear(iso: string | null | undefined, locale?: string): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  try {
    return new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' }).format(date);
  } catch {
    return date.toISOString().slice(0, 7);
  }
}

export function buildCanonicalNameIndex(devices: VerifiedDeviceEntry[]): Map<string, VerifiedDeviceEntry> {
  const index = new Map<string, VerifiedDeviceEntry>();
  for (const device of devices) {
    const keys = new Set<string>([
      device.canonical_name,
      device.device_name,
      device.manufacturer && device.device_name ? `${device.manufacturer} ${device.device_name}` : '',
      device.manufacturer,
    ].map((k) => normalizeRendererNameForMatch(k)).filter(Boolean));

    for (const key of keys) {
      if (!index.has(key)) index.set(key, device);
    }
  }
  return index;
}

export function matchZoneNameToVerified(
  zoneName: string | null | undefined,
  index: Map<string, VerifiedDeviceEntry>,
): VerifiedDeviceEntry | null {
  const cleaned = normalizeRendererNameForMatch(zoneName);
  if (!cleaned) return null;
  return index.get(cleaned) || null;
}

function modelTokens(model: string): string[] {
  return model
    .toLowerCase()
    .split(/[\s/_-]+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 3);
}

/**
 * Match a live renderer name to the verified catalog (exact, then manufacturer+model contains).
 */
export function matchZoneToVerifiedCatalog(
  zoneName: string | null | undefined,
  manufacturerHint: string | null | undefined,
  devices: VerifiedDeviceEntry[],
): VerifiedDeviceEntry | null {
  const normalized = normalizeRendererNameForMatch(zoneName);
  if (!normalized) return null;

  const index = buildCanonicalNameIndex(devices);
  const exact = matchZoneNameToVerified(zoneName, index);
  if (exact) return exact;

  const hint = normalizeRendererNameForMatch(manufacturerHint);

  for (const device of devices) {
    const mfr = normalizeRendererNameForMatch(device.manufacturer);
    const model = normalizeRendererNameForMatch(device.device_name);
    if (!model) continue;

    const fullName = mfr ? `${mfr} ${model}` : model;
    if (normalized === fullName || normalized === model) return device;

    const tokens = modelTokens(device.device_name);
    const distinctive = tokens.find((t) => t.length >= 5) || (model.length >= 6 ? model : null);

    if (mfr && normalized.includes(mfr)) {
      if (normalized.includes(model)) return device;
      if (tokens.some((t) => t.length >= 4 && normalized.includes(t))) return device;
    }

    if (hint && hint === mfr && distinctive && normalized.includes(distinctive)) {
      return device;
    }

    if (distinctive && normalized.includes(distinctive)) {
      if (!mfr || normalized.includes(mfr) || distinctive.length >= 6) {
        return device;
      }
    }
  }

  return null;
}

export interface LiveZoneProfile {
  zone_id: string;
  display_name?: string | null;
  discovered_name?: string | null;
}

export interface LiveDiscoveredZone {
  id?: string;
  name?: string;
  manufacturer?: string;
  vendor?: string;
  metadata?: {
    manufacturer?: string;
    model?: string;
    [key: string]: unknown;
  };
}

export interface ZoneVerifiedMatchResult {
  match: VerifiedDeviceEntry | null;
  rawName: string;
  canonical: string;
  manufacturerHint: string | null;
  triedNames: string[];
}

/** Collect live + profile name candidates — prefer connector discovery over stale profile fields. */
export function extractZoneMatchNames(
  profile: LiveZoneProfile,
  discovered?: LiveDiscoveredZone | null,
): string[] {
  const names: string[] = [];
  const push = (value?: string | null) => {
    const cleaned = cleanDisplayName(value);
    if (!cleaned) return;
    if (!names.some((n) => n.toLowerCase() === cleaned.toLowerCase())) {
      names.push(cleaned);
    }
  };

  push(discovered?.name);
  push(profile.discovered_name);
  push(profile.display_name);

  const mfr = extractZoneManufacturerHint(discovered);
  if (discovered?.name && mfr) {
    push(`${mfr} ${discovered.name}`);
  }
  if (profile.discovered_name && mfr) {
    push(`${mfr} ${profile.discovered_name}`);
  }

  return names;
}

export function extractZoneManufacturerHint(
  discovered?: LiveDiscoveredZone | null,
): string | null {
  const hint = cleanDisplayName(
    discovered?.metadata?.manufacturer
    || discovered?.manufacturer
    || discovered?.vendor,
  );
  return hint || null;
}

/**
 * Match a Devices-page zone row using live discovery shape + saved profile fields.
 */
export function matchLiveZoneToVerifiedCatalog(
  profile: LiveZoneProfile,
  discovered: LiveDiscoveredZone | null | undefined,
  devices: VerifiedDeviceEntry[],
): ZoneVerifiedMatchResult {
  const manufacturerHint = extractZoneManufacturerHint(discovered);
  const triedNames = extractZoneMatchNames(profile, discovered);

  for (const rawName of triedNames) {
    const match = matchZoneToVerifiedCatalog(rawName, manufacturerHint, devices);
    if (match) {
      return {
        match,
        rawName,
        canonical: normalizeRendererNameForMatch(rawName),
        manufacturerHint,
        triedNames,
      };
    }
  }

  const fallback = triedNames[0] || cleanDisplayName(profile.zone_id);
  return {
    match: null,
    rawName: fallback,
    canonical: normalizeRendererNameForMatch(fallback),
    manufacturerHint,
    triedNames,
  };
}

export function debugLogVerifiedZoneReconcile(
  zoneId: string,
  result: ZoneVerifiedMatchResult,
  registryCount: number,
): void {
  console.debug('[Maestro Verified] zone reconcile', {
    zoneId,
    rawName: result.rawName,
    canonical: result.canonical,
    manufacturerHint: result.manufacturerHint,
    triedNames: result.triedNames,
    registryCount,
    matched: result.match
      ? `${result.match.manufacturer} ${result.match.device_name}`
      : null,
  });
}
